import { useState } from "react";
import { withTranslation, TFunction } from "react-i18next";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useMetalPrices } from "../../contexts/MetalPriceContext";
import { ROUTES } from "../../config/site";
import {
  HeaderSection,
  TopDeskBar,
  CurrencySwitchButton,
  MainNavRow,
  LogoContainer,
  DesktopNav,
  NavRouteLink,
  NavCtaLink,
  MobileMenuTrigger,
  MobileDrawerPanel,
} from "./styles";

const Header = ({ t }: { t: TFunction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { metalData } = useMetalPrices();
  const { currency, toggleCurrency } = useCurrency();

  const formatRate = (symbol: string): string => {
    if (!metalData) return "---";
    const metal = metalData.metals.find((m) => m.symbol === symbol);
    if (!metal) return "---";
    const price = currency === "USD" ? metal.price : metal.priceAED;
    const unitRaw = currency === "USD" ? metal.unit : metal.unitAED;
    const unitPart = unitRaw.includes("/") ? unitRaw.split("/")[1] : "MT";
    const prefix = currency === "USD" ? "$" : "AED ";
    return `${prefix}${price.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}/${unitPart}`;
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <HeaderSection>
      <TopDeskBar>
        <div className="desk-inner">
          <div className="desk-rates">
            <span className="desk-rate-item">
              Copper <strong>{formatRate("COPPER")}</strong>
            </span>
            <span className="desk-rate-item">
              Aluminium <strong>{formatRate("ALUMINUM")}</strong>
            </span>
            <span className="desk-rate-item hide-mobile">
              Nickel <strong>{formatRate("NICKEL")}</strong>
            </span>
            <span className="desk-rate-item hide-tablet">
              Zinc <strong>{formatRate("ZINC")}</strong>
            </span>
            <span className="desk-rate-item hide-tablet">
              Lead <strong>{formatRate("LEAD")}</strong>
            </span>
          </div>
          <CurrencySwitchButton
            type="button"
            onClick={toggleCurrency}
            aria-label="Toggle pricing currency between USD and AED"
          >
            LME Desk: {currency} (Switch to {currency === "USD" ? "AED" : "USD"})
          </CurrencySwitchButton>
        </div>
      </TopDeskBar>

      <MainNavRow>
        <LogoContainer to="/" aria-label="Good Luck Metals homepage" onClick={closeMenu}>
          <img
            src="/img/logo.png"
            alt="Good Luck Metals emblem"
            width="44"
            height="44"
          />
          <div className="brand-lockup">
            <span className="brand-title">
              GOOD LUCK <span>METALS</span>
            </span>
            <span className="brand-sub">Deira, Dubai Scrap Trading &amp; Export</span>
          </div>
        </LogoContainer>

        <DesktopNav aria-label="Primary navigation">
          <NavRouteLink to={ROUTES.materials.path}>{t("Materials")}</NavRouteLink>
          <NavRouteLink to={ROUTES.uaeEmirates.path}>{t("UAE Coverage")}</NavRouteLink>
          <NavRouteLink to={ROUTES.recycling.path}>{t("Recycling")}</NavRouteLink>
          <NavRouteLink to={ROUTES.resources.path}>{t("Resources")}</NavRouteLink>
          <NavRouteLink to={ROUTES.about.path}>{t("About Us")}</NavRouteLink>
          <NavCtaLink to={ROUTES.contact.path}>{t("Request Live Quote")}</NavCtaLink>
        </DesktopNav>

        <MobileMenuTrigger
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
          {menuOpen ? "Close" : "Menu"}
        </MobileMenuTrigger>
      </MainNavRow>

      {menuOpen && (
        <MobileDrawerPanel>
          <NavRouteLink to={ROUTES.materials.path} onClick={closeMenu}>
            {t("Materials")}
          </NavRouteLink>
          <NavRouteLink to={ROUTES.uaeEmirates.path} onClick={closeMenu}>
            {t("UAE Coverage")}
          </NavRouteLink>
          <NavRouteLink to={ROUTES.recycling.path} onClick={closeMenu}>
            {t("Recycling")}
          </NavRouteLink>
          <NavRouteLink to={ROUTES.resources.path} onClick={closeMenu}>
            {t("Resources")}
          </NavRouteLink>
          <NavRouteLink to={ROUTES.about.path} onClick={closeMenu}>
            {t("About Us")}
          </NavRouteLink>
          <NavCtaLink to={ROUTES.contact.path} className="mobile-cta" onClick={closeMenu}>
            {t("Request Live Quote")}
          </NavCtaLink>
        </MobileDrawerPanel>
      )}
    </HeaderSection>
  );
};

export default withTranslation()(Header);
